/****************************************************
 * Module Name:  nivodaStock.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { Box, TextField, Tooltip, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import Select from "react-select";
import Constants from "../../utility/constants";
import { DataGridPro, gridClasses, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import NoPermission from "../common/noPermission";

import NivodaProxy from "../../utility/proxy/manager/nivodaProxy";
import { styled } from '@mui/system';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LoadingButton } from "@mui/lab";
import { SearchOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import PageTitle from "../common/pageTitle";

const StyledDataGridPro = styled(DataGridPro)({
    '.MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
        display: 'none',
    },
    '.MuiDataGrid-menuIcon': {
        width: '1px',
        fontSize: '1px'
    },
    [`& .${gridClasses.menuIconButton}`]: {
        marginRight: '-5px',
        fontSize: '1px',
        width: '1px',
        height: '1px',
    }
})

const NivodaStock = () => {

    const stoneTypeOptions = [
        { label: "Natural Diamonds", value: "Natural Diamonds" },
        { label: "Lab Grown Diamonds", value: "Lab Grown Diamonds" }
    ];

    const [stoneType, setStoneType] = useState(stoneTypeOptions[0]);
    const [stockData, setStockData] = useState([]);
    const [dataLoader, setDataLoader] = useState(false);
    const [stockPattern, setStockPattern] = useState("STOCK_");
    const [toDate, setToDate] = useState(new Date());
    const [filterValues, setFilterValues] = useState({});

    useEffect(() => {
        PageTitle("ANALYSIS_NIVODA_STOCK");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.ANALYSIS_NIVODA_STOCK
        )
        if (!allowed) {
            return <NoPermission />
        }
    }

    const getNivodaStock = () => {
        setDataLoader(true);
        const todate = Utility.ConvertLocalTimeToUtc(toDate).slice(0, 11) + "23:59:59.999Z";
        NivodaProxy.GetNivodaStockByStoneType(stoneType.value, todate, stockPattern, (response) => {
            if (response) {
                const uniqueFilterValues = {
                    lab: Utility.getDistinctOptions(response, "lab"),
                    shape: Utility.getDistinctOptions(response, "shape"),
                    clarity: Utility.getDistinctOptions(response, "clarity"),
                    color: Utility.getDistinctOptions(response, "color"),
                    cut: Utility.getDistinctOptions(response, "cut"),
                    pol: Utility.getDistinctOptions(response, "pol"),
                    sym: Utility.getDistinctOptions(response, "sym"),
                    flo: Utility.getDistinctOptions(response, "flo"),
                    location: Utility.getDistinctOptions(response, "location"),
                    status: Utility.getDistinctOptions(response, "status"),
                    luster: Utility.getDistinctOptions(response, "luster"),
                    eyeclean: Utility.getDistinctOptions(response, "eyeclean"),
                    shade: Utility.getDistinctOptions(response, "shade"),
                };
                setFilterValues(uniqueFilterValues);
                setStockData(response);
                setDataLoader(false);
            }
        })
    }

    const columns = [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "stockid",
            headerName: "STOCK ID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 90,
        },
        {
            field: "iteration",
            headerName: "DT FCH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.status : [],
            filterOperators: Utility.combinedFilters(),
            width: 90,
            renderCell: renderCellExpand,
        },
        {
            field: "carat",
            headerName: "CTS",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lab : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 65,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "clarity",
            headerName: "CLR",
            headerAlign: "center",
            align: "center",
            width: 55,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "color",
            headerName: "COL",
            headerAlign: "center",
            align: "center",
            width: 10,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 30,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 30,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 30,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "stonetable",
            headerName: "STONE TABLE",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "girdle",
            headerName: "GIRDLE",
            headerAlign: "center",
            align: "center",
            width: 110,
            renderCell: renderCellExpand,
        },
        {
            field: "location",
            headerName: "LOC",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.location : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "our",
            headerName: "OUR",
            headerAlign: "center",
            align: "center",
            width: 30,
            type: "Boolean",
            renderCell: (params) => params.value === true ? "YES" : "NO",
        },
        {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            width: 130,
            renderCell: renderCellExpand,
        },
        {
            field: "depth",
            headerName: "DEP",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80,
        },
        {
            field: "ratio",
            headerName: "RATIO",
            headerAlign: "center",
            align: "center",
            width: 80,
        },
        {
            field: "luster",
            headerName: "LUSTER",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.luster : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "eyeclean",
            headerName: "EYE CLEAN",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.eyeclean : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shade : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 40,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "priceperct",
            headerName: "PPC",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 60,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "diamondprice",
            headerName: "Diamond Price",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 110,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "stonetype",
            headerName: "STONE TYPE",
            headerAlign: "center",
            align: "center",
            width: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "createdate",
            headerName: "CREATEDAT",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        {
            field: "information",
            headerName: "INFO",
            headerAlign: "center",
            align: "center",
            width: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "supplierinfo",
            headerName: "SUPPLIER INFO",
            headerAlign: "center",
            align: "center",
            width: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "other",
            headerName: "OTHER",
            headerAlign: "center",
            align: "center",
            width: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "treatment",
            headerName: "TREATMENT",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            field: "criteriaid",
            headerName: "CRITERIA ID",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
    ];

    const rows = stockData;
    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
                {dataLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => getNivodaStock()}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        );
    }
    return (
        <Box>
            {
                <Box px={1}>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "15px",
                            paddingY: "10px",
                            justifyContent: { xs: "center", sm: "flex-start" },
                        }}
                    >
                        {/* Stone Type */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                flexDirection: { xs: "column", sm: "row" },
                                minWidth: { xs: "100%", sm: "auto" },
                            }}
                        >
                            <Typography>Stone Type</Typography>
                            <Select
                                menuPlacement="auto"
                                blurInputOnSelect={false}
                                className="basic-single"
                                classNamePrefix="select"
                                value={stoneType}
                                styles={{ ...Constants.Styles, container: (base) => ({ ...base, minWidth: 200 }) }}
                                isSearchable={true}
                                name="stone type"
                                menuPortalTarget={document.body}
                                options={stoneTypeOptions}
                                onChange={(selectedOption) => setStoneType(selectedOption)}
                            />
                        </Box>

                        {/* Date Picker */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                flexDirection: { xs: "column", sm: "row" },
                                minWidth: { xs: "90%", sm: "auto" },
                            }}
                        >
                            <Typography>Date</Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    className="picker"
                                    value={toDate}
                                    onChange={(newValue) => {
                                        const now = dayjs();
                                        const toDate = dayjs(newValue)
                                            .hour(now.hour())
                                            .minute(now.minute())
                                            .second(now.second())
                                            .millisecond(now.millisecond())
                                            .toDate();
                                        setToDate(toDate);
                                    }}
                                    renderInput={(props) => (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            {...props}
                                            inputProps={{ ...props.inputProps, readOnly: true }}
                                        />
                                    )}
                                    inputFormat="DD/MM/YYYY"
                                />
                            </LocalizationProvider>
                        </Box>

                        {/* Stock Pattern */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                flexDirection: { xs: "column", sm: "row" },
                                minWidth: { xs: "90%", sm: "auto" },
                            }}
                        >
                            <Typography>Stock Pattern</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter Stock Pattern"
                                value={stockPattern}
                                autoFocus
                                onChange={(e) =>
                                    setStockPattern(e.target.value)
                                }
                            />
                        </Box>

                        {/* Search Button */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: { xs: "100%", sm: "auto" },
                            }}
                        >
                            <LoadingButton
                                loading={dataLoader}
                                loadingPosition="start"
                                variant="contained"
                                onClick={() => getNivodaStock()}
                                startIcon={<SearchOutlined size="large" />}
                                sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                                Search
                            </LoadingButton>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .lavenderCell": {
                                color: "#080808",
                                backgroundColor: "#D8BFD8",
                            },
                            "& .greenCell": {
                                color: "#080808",
                                backgroundColor: "#B6D7A8"
                            },
                            "& .redCell": {
                                color: "#080808",
                                backgroundColor: "#f5d7d7",
                            },
                            "& .yellowCell": {
                                color: "#080808",
                                backgroundColor: "#F1E68C",
                            },
                            height: "80vh",
                        }}
                    >
                        <StyledDataGridPro
                            disableRowSelectionOnClick={true}
                            density="compact"
                            loading={dataLoader}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },
                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            slots={{ toolbar: CustomToolbar }}
                            checkboxSelection={true}
                            rows={rows}
                            columns={columns}
                            getRowId={(row) => row.id}
                            rowSpacingType="border"
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns status and traderName, the other columns will remain visible
                                        id: false,
                                        stockid: false,
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />
                    </Box>
                </Box>
            }
        </Box >
    )
}

export default NivodaStock;